import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Heart, Pencil, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SAVE_DEBOUNCE_MS = 1500;

/**
 * Single auto-saving reflection textarea.
 * Saves 1.5s after typing stops via PUT /api/menopause/answers.
 */
function ReflectionField({ moduleId, question, initialValue }) {
  const [value, setValue] = useState(initialValue || "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);
  const lastSent = useRef(initialValue || "");

  const persist = useCallback(
    async (text) => {
      if (text === lastSent.current) return;
      setStatus("saving");
      try {
        await api.put("/menopause/answers", {
          module_id: moduleId,
          question_id: question.id,
          answer_text: text,
        });
        lastSent.current = text;
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    },
    [moduleId, question.id],
  );

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setStatus("idle");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  };

  const onBlur = () => {
    if (timer.current) clearTimeout(timer.current);
    persist(value);
  };

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return (
    <div className="bg-white border border-hairline rounded-2xl p-6 md:p-8" data-testid={`tmi-reflection-${question.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <p className="font-serif-display text-lg md:text-xl text-ink leading-snug flex-1">
          {question.text}
        </p>
        <span className="text-xs text-ink-soft mt-1 inline-flex items-center gap-1.5 whitespace-nowrap">
          {status === "saving" && (
            <>
              <Loader2 size={12} className="animate-spin" /> Saving…
            </>
          )}
          {status === "saved" && (
            <>
              <Check size={12} className="text-sage" /> Saved
            </>
          )}
          {status === "error" && <span className="text-red-700">Couldn't save</span>}
        </span>
      </div>
      <textarea
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Take your time. There's no right answer."
        rows={5}
        className="w-full bg-cream-soft border border-hairline focus:border-sage focus:outline-none rounded-xl p-4 text-ink leading-relaxed resize-y placeholder-ink-soft/60"
        data-testid={`tmi-reflection-textarea-${question.id}`}
      />
    </div>
  );
}

export default function MenopauseModuleView() {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [allModules, setAllModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [{ data: mod }, { data: ans }, { data: all }] = await Promise.all([
          api.get(`/menopause/modules/${moduleId}`),
          api.get("/menopause/answers"),
          api.get("/menopause/modules"),
        ]);
        if (cancelled) return;
        setModule(mod);
        setSavedAnswers(ans?.[moduleId] || {});
        setAllModules(all || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Module not available.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Find next module for navigation
  const nextModule = (() => {
    if (!module || !allModules.length) return null;
    const idx = allModules.findIndex((m) => m.id === module.id);
    return idx >= 0 && idx < allModules.length - 1 ? allModules[idx + 1] : null;
  })();
  const prevModule = (() => {
    if (!module || !allModules.length) return null;
    const idx = allModules.findIndex((m) => m.id === module.id);
    return idx > 0 ? allModules[idx - 1] : null;
  })();

  return (
    <div className="min-h-screen bg-cream" data-testid="page-tmi-module">
      <header className="bg-cream-soft border-b border-hairline sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/dashboard/menopause"
            className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm"
            data-testid="tmi-module-back"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to overview
          </Link>
          <Link to="/" className="font-serif-display text-xl text-ink">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {loading && <div className="text-ink-soft">Loading…</div>}
        {error && <div className="text-red-700">{error}</div>}
        {module && (
          <>
            <div className="eyebrow mb-4 text-sage">{module.subtitle}</div>
            <h1
              className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.03]"
              data-testid="tmi-module-title"
            >
              {module.title}
            </h1>
            <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed text-lg">
              {module.description}
            </p>
            {module.duration && (
              <div className="mt-6 inline-flex items-center gap-2 text-xs text-ink-soft border border-hairline rounded-full px-4 py-1.5">
                <BookOpen size={12} strokeWidth={1.5} /> {module.duration}
              </div>
            )}

            {/* Teaching */}
            {module.teaching?.length > 0 && (
              <section className="mt-14" data-testid="tmi-module-teaching">
                <div className="eyebrow mb-4 text-sage flex items-center gap-2">
                  <Heart size={12} strokeWidth={1.8} /> The Teaching
                </div>
                <div className="bg-white border border-hairline rounded-2xl p-8 md:p-12 space-y-6 text-ink leading-relaxed text-[1.02rem]">
                  {module.teaching.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </section>
            )}

            {/* Reflection questions — auto-saving textareas */}
            {module.questions?.length > 0 && (
              <section className="mt-14" data-testid="tmi-module-reflections">
                <div className="eyebrow mb-4 text-sage flex items-center gap-2">
                  <Pencil size={12} strokeWidth={1.8} /> Your Reflections
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-3">
                  Sit with these — without rushing.
                </h2>
                <p className="text-ink-soft max-w-xl mb-8 leading-relaxed">
                  Your answers save automatically as you type. They're private to you and will be
                  here whenever you return.
                </p>
                <div className="space-y-5">
                  {module.questions.map((q) => (
                    <ReflectionField
                      key={q.id}
                      moduleId={module.id}
                      question={q}
                      initialValue={savedAnswers[q.id] || ""}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Integration */}
            {module.integration?.length > 0 && (
              <section className="mt-14" data-testid="tmi-module-integration">
                <div className="eyebrow mb-4 text-sage">Integration</div>
                <div className="bg-cream-soft border-l-4 border-sage rounded-r-2xl p-8 md:p-10 space-y-5 text-ink leading-relaxed">
                  {module.integration.map((p, i) => (
                    <p key={i} className={p.startsWith("Love,") ? "font-serif-display text-xl italic text-sage" : ""}>
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            )}

            {/* Module nav */}
            <div className="mt-16 flex flex-col sm:flex-row items-stretch gap-4">
              {prevModule ? (
                <Link
                  to={`/dashboard/menopause/module/${prevModule.id}`}
                  className="flex-1 inline-flex items-center justify-start gap-3 border border-hairline hover:border-sage rounded-2xl p-5 text-left transition-all"
                  data-testid="tmi-module-prev"
                >
                  <ArrowLeft size={16} strokeWidth={1.6} className="text-sage" />
                  <div>
                    <div className="text-xs text-ink-soft uppercase tracking-wider">Previous</div>
                    <div className="font-serif-display text-lg text-ink leading-tight">
                      {prevModule.title}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
              {nextModule ? (
                <Link
                  to={`/dashboard/menopause/module/${nextModule.id}`}
                  className="flex-1 inline-flex items-center justify-end gap-3 bg-sage hover:bg-sage-dark text-cream rounded-2xl p-5 text-right transition-all"
                  data-testid="tmi-module-next"
                >
                  <div>
                    <div className="text-xs text-cream/70 uppercase tracking-wider">Next</div>
                    <div className="font-serif-display text-lg leading-tight">{nextModule.title}</div>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.6} />
                </Link>
              ) : (
                <Link
                  to="/dashboard/menopause"
                  className="flex-1 inline-flex items-center justify-end gap-3 bg-gold hover:bg-gold-dark text-ink rounded-2xl p-5 text-right transition-all"
                  data-testid="tmi-module-finish"
                >
                  <div>
                    <div className="text-xs text-ink/70 uppercase tracking-wider">Complete</div>
                    <div className="font-serif-display text-lg leading-tight">Back to overview</div>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.6} />
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
